import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { useAuth } from '../../../providers/AuthProvider';
import { UserImageCircleNEW } from '../../../components/UserImageCircle';
import ApplicationStepper from '../../talent-application/components/ApplicationStepper';
import ReadyToSubmitProfileModal from '../../talent-application/ReadyToSubmitProfileModal';

const UNLOCKS = [
    'Job Board',
    'Project Matches',
    'Proposals',
    'Contracts',
    'Teams',
    'Notifications',
];

const EDIT_PROFILE_PATH = '/talent/application';

/**
 * Resolve which pre-approval state the talent user is in.
 *
 * @param {Object} auth
 * @returns {String|null}
 */
const resolveState = (auth) => {
    if (!auth || auth.isProfileApproved()) return null;
    if (!auth.isApplicationApproved()) return null;
    if (auth.latest_appeal?.is_rejected) return 'appeal_rejected';
    if (auth.latest_appeal) return 'appeal_pending';
    if (auth.isPendingProfileApproved()) return 'under_review';
    if (auth.isProfileRejected()) return 'rejected';
    if (auth.isProfileReadyForApproval()) return 'ready_for_approval';
    return 'in_progress';
};

const primaryBtn = 'inline-flex items-center text-white text-sm font-semibold px-5 py-2.5 rounded-lg whitespace-nowrap no-underline hover:no-underline hover:text-white transition-colors bg-[#7C3AED] hover:bg-purple-700';
const softBtn = 'inline-flex items-center text-sm font-semibold px-5 py-2.5 rounded-lg whitespace-nowrap no-underline hover:no-underline transition-colors bg-purple-100 text-purple-700 border border-purple-200 hover:bg-purple-200 hover:text-purple-800';
const outlineBtn = 'inline-flex items-center text-sm font-semibold px-5 py-2.5 rounded-lg whitespace-nowrap no-underline hover:no-underline transition-colors border border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900';

/**
 * Per-state copy and actions. `editsProfile` marks states whose primary
 * action already goes to the profile editor, so we don't render a
 * duplicate "Edit Profile" button next to it.
 */
const buildConfig = (state, { auth, openSubmitModal }) => ({
    ready_for_approval: {
        iconClass: 'bg-amber-500 text-white',
        icon: <span className="text-lg font-bold">!</span>,
        title: 'Your profile is ready — submit it for approval',
        desc: "You're 100% complete. Clients can't see you until Wripple reviews and approves your profile (1–2 business days).",
        action: (
            <button type="button" onClick={openSubmitModal} className={primaryBtn}>
                Submit for Approval →
            </button>
        ),
    },
    in_progress: {
        iconClass: 'bg-gray-100 text-gray-500',
        icon: <span className="text-sm font-bold">%</span>,
        title: 'Complete your profile to submit for approval',
        desc: 'Finish the remaining sections so your profile can be reviewed by the Wripple team.',
        editsProfile: true,
        action: (
            <Link to={EDIT_PROFILE_PATH} className={primaryBtn}>
                Continue Profile
            </Link>
        ),
    },
    under_review: {
        iconClass: 'bg-purple-500 text-white',
        icon: <i className="fas fa-hourglass-half text-base" />,
        title: 'Your profile is under review',
        desc: "We'll email you once Wripple has reviewed your profile. This typically takes 1–2 business days.",
        action: (
            <Link to={`/talent-profiles/${auth.id}`} className={softBtn}>
                Preview Client View
            </Link>
        ),
    },
    rejected: {
        iconClass: 'bg-red-100 text-red-500',
        icon: <i className="fas fa-times-circle text-lg" />,
        title: 'Your profile was not approved',
        desc: 'Visit your profile to view feedback and submit an appeal for further review.',
        editsProfile: true,
        action: (
            <Link to={EDIT_PROFILE_PATH} className={primaryBtn}>
                View Feedback
            </Link>
        ),
    },
    appeal_pending: {
        iconClass: 'bg-orange-100 text-orange-500',
        icon: <i className="fas fa-history text-lg" />,
        title: 'Your appeal is under review',
        desc: 'A talent manager will review your appeal and reach out via email.',
        action: null,
    },
    appeal_rejected: {
        iconClass: 'bg-red-100 text-red-500',
        icon: <i className="fas fa-times-circle text-lg" />,
        title: 'Your appeal was not approved',
        desc: 'Visit your profile to submit a new appeal for further review.',
        editsProfile: true,
        action: (
            <Link to={EDIT_PROFILE_PATH} className={primaryBtn}>
                View Profile
            </Link>
        ),
    },
}[state]);

/**
 * Talent dashboard shown before the profile is approved. Combines the
 * status message, application stepper, profile checklist and the list of
 * features that unlock once live into a single panel.
 *
 * @param {Object} props
 * @param {String} [props.colClass]
 */
const PendingApprovalDashboard = ({ colClass = 'w-full mb-6 sm:px-3' }) => {
    const { auth } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    if (!auth) return null;

    const state = resolveState(auth);

    const openSubmitModal = () => {
        const params = new URLSearchParams(searchParams);
        params.set('submitForApproval', '1');
        setSearchParams(params);
    };

    const config = state ? buildConfig(state, { auth, openSubmitModal }) : null;

    const progress = auth.profileProgress?.() ?? 0;
    const stepSections = TB.groupBy(
        auth.getFullProfileStepsForTalent(),
        step => step.StepSection
    );

    return (
        <div className={colClass}>
            <div className="talent-dashboard-panel !min-h-[unset] !p-0 overflow-hidden">

                {/* Status hero */}
                <div className="px-5 pt-6 pb-5 sm:px-8 sm:pt-8">
                    {config && (
                        <div className="flex items-start gap-4">
                            <div className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center ${config.iconClass}`}>
                                {config.icon}
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-xl sm:text-2xl font-semibold leading-tight text-gray-900 mb-1">
                                    {config.title}
                                </h2>
                                <p className="text-sm sm:text-base text-gray-600 leading-snug mb-0 max-w-2xl">
                                    {config.desc}
                                </p>

                                <div className="flex flex-wrap gap-3 pt-4">
                                    {config.action}
                                    {!config.editsProfile && (
                                        <Link to={EDIT_PROFILE_PATH} className={outlineBtn}>
                                            {TB.trans('talent.dashboard.profile_overview_buttons.edit_profile')}
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={TB.mapClass('', { 'pt-6 mt-6 border-t border-gray-100': !!config })}>
                        <ApplicationStepper />
                    </div>
                </div>

                {/* Profile + unlocks */}
                <div className="grid gap-6 px-5 py-5 sm:px-8 bg-gray-50 border-t border-gray-100 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">

                    <div className="flex flex-col sm:flex-row gap-4 sm:items-center min-w-0">
                        <div className="flex items-center gap-3 flex-shrink-0">
                            <UserImageCircleNEW
                                user={auth}
                                className="!h-[56px] !w-[56px] text-xl"
                            />
                            <div className="min-w-0">
                                <p className="font-semibold text-gray-900 leading-tight mb-0 truncate">
                                    {auth.fullName()}
                                </p>
                                {auth.job_title && (
                                    <p className="text-sm text-gray-500 leading-tight mb-0 truncate">
                                        {auth.job_title}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="flex-1 min-w-0 sm:pl-4 sm:border-l sm:border-gray-200">
                            <div className="flex items-center gap-3 mb-2">
                                <span className="text-xs font-bold uppercase tracking-wide text-gray-700 whitespace-nowrap">
                                    {`${progress}% complete`}
                                </span>
                                <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
                                    <div
                                        className="h-full rounded-full"
                                        style={{
                                            width: `${progress}%`,
                                            background: TB.config('color.light_green'),
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                                {TB.keys(stepSections).map(sectionKey => {
                                    const steps = stepSections[sectionKey];
                                    const firstStep = TB.first(steps);
                                    const hasCompleted = !TB.filter(
                                        steps,
                                        step => step.NeedsCompletion?.(auth) !== false && !step.hasCompleted(auth)
                                    )?.length;

                                    return (
                                        <Link
                                            key={firstStep.path}
                                            to={firstStep.getPath()}
                                            title={firstStep.StepTitle}
                                            className="inline-flex items-center gap-1 text-xs text-gray-700 hover:text-gray-900 no-underline hover:no-underline"
                                        >
                                            {hasCompleted
                                                ? <Check className="h-3.5 w-3.5 text-success" strokeWidth={3} />
                                                : <X className="h-3.5 w-3.5 text-danger" strokeWidth={3} />}
                                            {sectionKey}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="min-w-0 lg:pl-6 lg:border-l lg:border-gray-200">
                        <p className="text-xs font-bold uppercase tracking-wide text-gray-700 mb-2">
                            Once you're live, you'll unlock
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {UNLOCKS.map(item => (
                                <span
                                    key={item}
                                    className="inline-flex items-center gap-1 text-xs text-gray-600 bg-white border border-gray-200 rounded-full px-2.5 py-1"
                                >
                                    <i className="fas fa-lock text-[9px] text-gray-400" />
                                    {item}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <ReadyToSubmitProfileModal onSubmitProfileApproval={() => { }} />
        </div>
    );
};

export default PendingApprovalDashboard;
